// CyclingWord.tsx - fait défiler une liste de mots (ex. villes actives) par fondu.
// Choix d'implémentation : grille superposée (tous les mots dans la même cellule)
//  → la largeur s'ajuste sur le mot le plus large (aucun clip) ET la ligne de base
//    reste celle du texte environnant (pas d'overflow:hidden, pas de transform qui
//    décaleraient le mot). Respecte prefers-reduced-motion (fige sur le 1er mot).
import { useEffect, useRef, useState } from "react";
import type { CSSProperties, ReactElement } from "react";

export interface CyclingWordProps {
  words: string[];
  interval?: number;
  color?: string;
  style?: CSSProperties;
}

function usePrefersReducedMotion(): boolean {
  const [reduce, setReduce] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const on = () => setReduce(mq.matches);
    on();
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);
  return reduce;
}

export function CyclingWord({
  words,
  interval = 2200,
  color = "currentColor",
  style,
}: CyclingWordProps): ReactElement {
  const list = words && words.length ? words : [""];
  const reduce = usePrefersReducedMotion();
  const [i, setI] = useState(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (reduce || list.length < 2) return;
    intervalRef.current = setInterval(() => {
      setI((p) => (p + 1) % list.length);
    }, interval);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [reduce, list.length, interval]);

  const active = reduce ? 0 : i % list.length;

  return (
    <span style={{ display: "inline-grid", ...style }}>
      {/* Tous les mots occupent la même cellule → largeur = mot le plus large,
          ligne de base commune ; seul l'actif est opaque (fondu). */}
      {list.map((w, idx) => {
        const isActive = idx === active;
        return (
          <span
            key={idx}
            aria-hidden={isActive ? undefined : true}
            style={{
              gridArea: "1 / 1",
              whiteSpace: "nowrap",
              color,
              opacity: isActive ? 1 : 0,
              transition: reduce ? "none" : "opacity .38s ease",
            }}
          >
            {w}
          </span>
        );
      })}
    </span>
  );
}
